import React, { useEffect, useState } from 'react';
import { Button } from '@habitech/call-to-action';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import SkeletonLoader from 'src/components/common/skeletonLoading';
import Icon from 'src/components/common/Icon';
import Modal from 'src/components/common/modal/Modal';
import {
    selectPortfolioPage,
    selectPortfolioPageSize,
    selectPortfolioSaveError,
    selectPortfolioSaving,
    selectPortfolioSlotFilter,
    selectPortfolioSlots,
    selectPortfolioSlotsOrder,
    selectPortfolioTotals,
} from 'src/redux/user_portfolio/user_portfolio.selectors';
import { getPortfolio, updateValidity } from 'src/redux/user_portfolio/thunks';
import { PAGE_SIZE_OPTIONS } from 'src/redux/user_portfolio/user_portfolio.constants';
import { SlotsOrder } from 'src/redux/user_portfolio/user_portfolio.types';
import SlotsTable from '../SlotsTable/SlotsTable';
import Pagination from '../Assignments/Pagination';
import { useLazyPortfolio } from '../hooks/useLazyPortfolio';
import {
    Actions,
    Field,
    FormRow,
    Intro,
    LoadingBar,
    SaveError,
    SelectionHint,
    SlotSearchField,
    SuccessModalContent,
    TableArea,
    TableFooter,
    TableToolbar,
    ValiditiesRoot,
} from './Validities.styles';

const EMPTY_COPY = 'Busca un cliente por correo electrónico para ver sus slots.';
const NO_SLOTS_COPY = 'Este cliente no tiene slots.';

/**
 * Tab "Vigencias": selecciona slots para cambiar su vigencia (nueva fecha de
 * expiración + motivo). Los datos vienen de `user_portfolio` (carga perezosa),
 * se pueden filtrar por slot vía el servicio (`slot_id`) y la acción dispara el
 * POST `commercial_panel/validity`.
 */
const Validities = () => {
    const dispatch = useAppDispatch();
    const { submittedQuery, loading, error } = useLazyPortfolio();
    const slots = useAppSelector(selectPortfolioSlots);
    const saving = useAppSelector(selectPortfolioSaving);
    const saveError = useAppSelector(selectPortfolioSaveError);
    const slotFilter = useAppSelector(selectPortfolioSlotFilter);
    const slotsOrder = useAppSelector(selectPortfolioSlotsOrder);
    const page = useAppSelector(selectPortfolioPage);
    const pageSize = useAppSelector(selectPortfolioPageSize);
    const { totalSlots } = useAppSelector(selectPortfolioTotals);

    // El backend pagina con `page` (0-based); el componente Pagination es 1-based.
    const showFooter = slotFilter === '' && slots.length > 0;

    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [expirationDate, setExpirationDate] = useState('');
    const [reason, setReason] = useState('');
    const [slotInput, setSlotInput] = useState(slotFilter);
    const [showSuccess, setShowSuccess] = useState(false);

    // Mantiene el input sincronizado con el filtro realmente aplicado (persiste
    // al alternar pestañas porque vive en redux).
    useEffect(() => {
        setSlotInput(slotFilter);
    }, [slotFilter]);

    const toggleSlot = (id: number) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
        );
    };

    const searchBySlot = (slotId: string) => {
        if (!submittedQuery) return;
        setSelectedIds([]);
        dispatch(getPortfolio({ parameter: submittedQuery, slotId: slotId.trim() }));
    };

    const handleSlotKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') searchBySlot(slotInput);
    };

    const handleSlotClear = () => {
        setSlotInput('');
        searchBySlot('');
    };

    const handleChangePage = (uiPage: string | number) => {
        if (!submittedQuery) return;
        setSelectedIds([]);
        dispatch(getPortfolio({ parameter: submittedQuery, page: Number(uiPage) - 1 }));
    };

    // Cambia el tamaño de página (server-side): recarga desde la página 0 con el
    // nuevo `page_size` para mantener sincronizado el total con Asignaciones.
    const handleChangePageSize = (nextSize: number) => {
        if (!submittedQuery) return;
        setSelectedIds([]);
        dispatch(getPortfolio({ parameter: submittedQuery, pageSize: nextSize, page: 0 }));
    };

    // Alterna el orden por fecha de expiración (asc -> desc -> asc). El backend
    // ordena globalmente vía `slots_order`, por eso se recarga desde la página 0.
    const handleToggleExpirationSort = () => {
        if (!submittedQuery) return;
        const nextOrder: SlotsOrder = slotsOrder === 'asc' ? 'desc' : 'asc';
        setSelectedIds([]);
        dispatch(getPortfolio({ parameter: submittedQuery, slotsOrder: nextOrder, page: 0 }));
    };

    const canSubmit = selectedIds.length > 0 && expirationDate !== '' && !saving;

    const handleSubmit = () => {
        if (!canSubmit) return;
        dispatch(
            updateValidity({
                slotIds: selectedIds,
                expirationDate,
                reason,
            })
        )
            .unwrap()
            .then(() => {
                setSelectedIds([]);
                setExpirationDate('');
                setReason('');
                setShowSuccess(true);
            })
            .catch(() => {
                /* El error se refleja vía saveError en el estado. */
            });
    };

    const handleNewChange = () => {
        setShowSuccess(false);
    };

    if (!submittedQuery) {
        return <ValiditiesRoot>{EMPTY_COPY}</ValiditiesRoot>;
    }

    if (error) {
        return <ValiditiesRoot>{error}</ValiditiesRoot>;
    }

    return (
        <ValiditiesRoot>
            <Intro>
                <Icon icon="pcom-icon-information-filled" size={16} />
                Selecciona los slots que quieres modificar y define la nueva fecha de vigencia.
            </Intro>

            <FormRow>
                <Field>
                    Nueva fecha de expiración
                    <input
                        type="date"
                        data-id="validity-expiration-date"
                        value={expirationDate}
                        onChange={(e) => setExpirationDate(e.target.value)}
                    />
                </Field>
                <Field>
                    Motivo
                    <textarea
                        data-id="validity-reason"
                        placeholder="Describe el motivo del cambio de vigencia"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </Field>
            </FormRow>

            {saveError && <SaveError>{saveError}</SaveError>}

            <Actions>
                <Button
                    type="button"
                    variant="primary"
                    size="large"
                    originType="pcom"
                    dataId="button-change-validity"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    loading={saving}
                >
                    Cambiar vigencia
                </Button>
            </Actions>

            <TableToolbar>
                <SlotSearchField>
                    Buscar por ID de slot
                    <input
                        type="text"
                        inputMode="numeric"
                        data-id="validity-slot-search"
                        placeholder="Ej. 382743"
                        value={slotInput}
                        onChange={(e) => setSlotInput(e.target.value.replace(/\D/g, ''))}
                        onKeyDown={handleSlotKeyDown}
                    />
                </SlotSearchField>
                <Button
                    type="button"
                    variant="stroke"
                    size="medium"
                    originType="pcom"
                    dataId="button-search-slot"
                    onClick={() => searchBySlot(slotInput)}
                    disabled={loading}
                >
                    Buscar slot
                </Button>
                {slotFilter !== '' && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="medium"
                        originType="pcom"
                        dataId="button-clear-slot"
                        onClick={handleSlotClear}
                        disabled={loading}
                    >
                        Limpiar filtro
                    </Button>
                )}
            </TableToolbar>

            {loading && <LoadingBar data-testid="validity-loading-bar" />}

            <SelectionHint>
                {selectedIds.length > 0
                    ? `${selectedIds.length} slot(s) seleccionado(s).`
                    : 'Selecciona al menos un slot para cambiar su vigencia.'}
            </SelectionHint>

            {loading && slots.length === 0 ? (
                <SkeletonLoader width="100%" height="20px" count={5} />
            ) : (
                <TableArea $loading={loading}>
                    <SlotsTable
                        slots={slots}
                        selectable
                        selectedIds={selectedIds}
                        onToggle={toggleSlot}
                        expirationSort={slotsOrder}
                        onToggleExpirationSort={handleToggleExpirationSort}
                        emptyCopy={
                            slotFilter !== '' ? 'No se encontró el slot buscado.' : NO_SLOTS_COPY
                        }
                    />
                </TableArea>
            )}

            {showFooter && (
                <TableFooter>
                    <Pagination
                        dataId="validity-pagination"
                        page={page + 1}
                        total={totalSlots}
                        pageSize={pageSize}
                        onPageChange={handleChangePage}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        onPageSizeChange={handleChangePageSize}
                    />
                </TableFooter>
            )}

            {showSuccess && (
                <Modal onClose={handleNewChange}>
                    <SuccessModalContent>
                        <Icon icon="pcom-icon-check-circle" size={48} className="success-icon" />
                        <span className="success-title">Cambio de vigencia exitoso</span>
                        <span className="success-subtitle">
                            La vigencia se actualizó correctamente.
                        </span>
                        <Button
                            type="button"
                            variant="primary"
                            size="large"
                            originType="pcom"
                            dataId="button-new-validity-change"
                            onClick={handleNewChange}
                        >
                            Realizar otro cambio
                        </Button>
                    </SuccessModalContent>
                </Modal>
            )}
        </ValiditiesRoot>
    );
};

export default Validities;
